import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { auth } from '../../../services/auth';
import { useStores } from '../../../stores/useStores';
import { getErrorMessage } from '../../../utils/errors';

import styles from './EmailVerification.module.scss';
import envelope from './envelope.png';

export const EmailVerification = observer(({ isCurrent, prevStep, nextStep }: WizardChildProps) => {
  const { authStore, portalStore, rewardsStore } = useStores();

  const { t } = useTranslation();

  const { sendVerificationEmailCooldown } = authStore;

  const [error, setError] = useState<string>('');

  const isProcessing = computed(() => authStore.pending('resendEmailVerification')).get();

  const handleGoBack = async () => {
    await auth.signOut();
    prevStep();
  };

  const handleClose = async () => {
    await auth.signOut();
    portalStore.closePortal();
  };

  const handleResend = async () => {
    try {
      await authStore.resendEmailVerification();
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
    }
  };

  useEffect(() => {
    if (isCurrent) {
      const unsub = authStore.trackEmailVerificationCompletion(() => {
        nextStep();
      });

      return () => unsub();
    }
  }, [authStore, nextStep, isCurrent, rewardsStore]);

  useEffect(() => {
    return () => authStore.clearSendVerificationEmailCooldown();
  }, [authStore]);

  return (
    <>
      <DialogHeader
        className={styles.title}
        title={t('auth.checkYourEmail')}
        titleDataTestId="email-verification-title"
        onClose={handleClose}
        onGoBack={handleGoBack}
      />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent className={styles.content}>
        <div className={styles.messageFirstLine}>{t('emailVerification.verifyYourAccount')}</div>
        <div className={styles.messageSecondLine}>{t('emailVerification.getWelcomeBonus')}</div>

        <img alt="envelope" className={styles.envelopeImage} src={envelope} />

        <p className={styles.sentConfirmationEmail}>
          {t('emailVerification.confirmationEmailSent')} <b>{authStore.currentUserEmail}</b>
        </p>

        <p className={styles.resendRow}>
          {t('emailVerification.notInInbox')}{' '}
          {isProcessing ? (
            <span className={styles.resending}>{t('emailVerification.resending')}</span>
          ) : (
            <>
              <span
                className={classNames('text-link', { [styles.resendBtnDisabled]: sendVerificationEmailCooldown > 0 })}
                onClick={handleResend}
              >
                {t('emailVerification.resend')}
              </span>
              {sendVerificationEmailCooldown > 0 ? ` in ${sendVerificationEmailCooldown}` : ''}
            </>
          )}
        </p>
        <ErrorMessage>{error}</ErrorMessage>
      </DialogContent>
    </>
  );
});
